import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  User,
  Calendar,
  Tag,
  Info,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ACTION_COLORS = {
  create_project:              'bg-green-100 text-green-800',
  archive_project:             'bg-blue-100 text-blue-800',
  delete_project:              'bg-red-100 text-red-800',
  cancel_project:              'bg-orange-100 text-orange-800',
  update_project:              'bg-yellow-100 text-yellow-800',
  update_status:               'bg-purple-100 text-purple-800',
  update_task:                 'bg-indigo-100 text-indigo-800',
  update_task_status:          'bg-cyan-100 text-cyan-800',
  reject_task:                 'bg-red-100 text-red-800',
  reject_approval:             'bg-rose-100 text-rose-800',
  approve_notification:        'bg-teal-100 text-teal-800',
  assign_task:                 'bg-lime-100 text-lime-800',
  unassign_task:               'bg-gray-100 text-gray-700',
  complexity_change:           'bg-fuchsia-100 text-fuchsia-800',
};

const getActionColor = (action) =>
  ACTION_COLORS[action] || 'bg-gray-100 text-gray-700';

const TARGET_TYPE_ICONS = {
  project: '📁',
  task:    '✅',
  user:    '👤',
  approval_notification: '🔔',
};

// ── Component ─────────────────────────────────────────────────────────────────

const AuditTrails = () => {
  const { token } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  // Filters
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Dropdown data
  const [availableActions, setAvailableActions] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  // Expanded rows (show full details JSON)
  const [expandedRows, setExpandedRows] = useState(new Set());

  // ── Fetch Helpers ──────────────────────────────────────────────────────────
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchMeta = useCallback(async () => {
    try {
      const [actRes, usrRes] = await Promise.all([
        api.get('/audit/actions', { headers: authHeaders }),
        api.get('/audit/users',   { headers: authHeaders }),
      ]);
      setAvailableActions(actRes.data || []);
      setAvailableUsers(usrRes.data || []);
    } catch (_) {
      // non-critical — filters still work without these
    }
  }, [token]); // eslint-disable-line

  const fetchLogs = useCallback(async (targetPage = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: targetPage, limit };
      if (search)          params.project_search = search;
      if (actionFilter)    params.action          = actionFilter;
      if (userFilter)      params.user_name       = userFilter;
      if (targetTypeFilter)params.target_type     = targetTypeFilter;
      if (dateFrom)        params.date_from       = dateFrom;
      if (dateTo)          params.date_to         = dateTo;

      const res = await api.get('/audit/logs', { headers: authHeaders, params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(targetPage);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [token, search, actionFilter, userFilter, targetTypeFilter, dateFrom, dateTo, limit]); // eslint-disable-line

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchLogs(1);
  };

  const clearFilters = () => {
    setSearch('');
    setActionFilter('');
    setUserFilter('');
    setTargetTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const hasActiveFilters =
    search || actionFilter || userFilter || targetTypeFilter || dateFrom || dateTo;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Audit Trails</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Full history of every action performed in the system
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{total.toLocaleString()} records</span>
          <button
            onClick={() => fetchLogs(page)}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Filter className="w-4 h-4" />
          Filters
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-2 text-xs text-red-500 hover:text-red-700 underline"
            >
              Clear all
            </button>
          )}
        </div>

        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Project, job code, task, user…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Action */}
          <div className="relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              <option value="">All Actions</option>
              {availableActions.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>

          {/* User */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              <option value="">All Users</option>
              {availableUsers.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          {/* Target type */}
          <div className="relative">
            <Info className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={targetTypeFilter}
              onChange={(e) => setTargetTypeFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              <option value="">All Types</option>
              <option value="project">Project</option>
              <option value="task">Task</option>
              <option value="user">User</option>
              <option value="approval_notification">Approval</option>
            </select>
          </div>

          {/* Date from */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date to */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Apply Filters
          </button>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading audit logs…
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ClipboardList className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No audit records match the current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Time</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">User</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Action</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Details</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const isExpanded = expandedRows.has(log.id);
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleRow(log.id)}
                      >
                        {/* Time */}
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap align-top">
                          {log.timestamp || '—'}
                        </td>

                        {/* User */}
                        <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap align-top">
                          {log.user_name || '—'}
                        </td>

                        {/* Action badge */}
                        <td className="px-4 py-3 align-top">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${getActionColor(log.action)}`}>
                            {log.action_label}
                          </span>
                        </td>

                        {/* Target type */}
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap align-top">
                          <span className="mr-1">{TARGET_TYPE_ICONS[log.target_type] || '🔹'}</span>
                          {log.target_type || '—'}
                        </td>

                        {/* Details summary */}
                        <td className="px-4 py-3 text-gray-600 max-w-xs align-top">
                          <span className="line-clamp-1">{log.details_text || '—'}</span>
                        </td>

                        {/* Expand toggle */}
                        <td className="px-4 py-3 text-gray-400 align-top">
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />}
                        </td>
                      </tr>

                      {/* Expanded row: full details */}
                      {isExpanded && (
                        <tr className="bg-blue-50 border-b border-blue-100">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Target ID</p>
                                <p className="text-xs text-gray-700 font-mono break-all">{log.target_id || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Full Details</p>
                                <pre className="text-xs text-gray-700 bg-white border border-gray-200 rounded p-2 overflow-auto max-h-40">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {pages} &bull; {total.toLocaleString()} total records
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchLogs(page - 1)}
              disabled={page <= 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>

            {/* Page numbers */}
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              let p;
              if (pages <= 5) {
                p = i + 1;
              } else if (page <= 3) {
                p = i + 1;
              } else if (page >= pages - 2) {
                p = pages - 4 + i;
              } else {
                p = page - 2 + i;
              }
              return (
                <button
                  key={p}
                  onClick={() => fetchLogs(p)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    p === page
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              );
            })}

            <button
              onClick={() => fetchLogs(page + 1)}
              disabled={page >= pages}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTrails;
